import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import { Popover as RNPopper } from 'react-native-popper'

import styled, { useTheme } from 'styled-components/native'

import { closeAccessibilityLabel } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'

import { useOutsideClickClose } from './useOutsideClickClose'
import { ButtonSize } from '../../atoms/baseButton/BaseButton'
import { CloseIcon } from '../../atoms/icons/CloseIcon'
import TertiaryIconButton from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { Size as TitleSize, Title as TitleText } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { RowView } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

const PopoverContentView = styled.View<{ width: number | string }>(({ theme, width }) => ({
  padding: theme.spacing['24px'],
  width,
  borderRadius: '16px',
  overflow: 'hidden',
  zIndex: 1,
  boxShadow: `0 2px 26px ${theme.colors.shadowLow}`,
  backgroundColor: theme.colors.backgroundPrimary,
}))

const HeaderRow = styled(RowView)(({ theme }) => ({
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing['8px'],
}))

const Title = styled(TitleText)({
  flex: 1,
})

const IconContainer = styled.View(({ theme }) => ({
  padding: theme.spacing['8px'],
}))

const ScrollableContent = styled.ScrollView({})

// These match the styled components above and are used when clamping the scroll body height.
const CONTENT_PADDING = 24 // PopoverContentView padding (top and bottom)
const HEADER_GAP = 8 // HeaderRow marginBottom
// Floor for the clamped scroll body so a tiny overlay still shows ~2 lines of content.
const MIN_SCROLL_BODY_HEIGHT = 80

export type PopoverProps = {
  title: React.ReactNode
  children: React.ReactNode
  trigger: React.ReactElement | React.RefObject<any>
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top left' | 'top right' | 'bottom left' | 'bottom right'
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  offset?: number
  crossOffset?: number
  width?: number | string
  testIdPrefix?: string
  positionKey?: string | number
  aboveHeaderContent?: React.ReactNode
}

export const Popover: FC<PopoverProps> = ({
  title,
  children,
  trigger,
  placement = 'bottom',
  isOpen: isOpenProp,
  onOpenChange,
  offset,
  crossOffset,
  width = '444px',
  testIdPrefix = 'Popover',
  positionKey,
  aboveHeaderContent,
}) => {
  const { colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()
  const [isOpenState, setIsOpenState] = useState(false)
  const [bodyMaxHeight, setBodyMaxHeight] = useState<number | undefined>(undefined)
  const [prevPositionKey, setPrevPositionKey] = useState<typeof positionKey>(positionKey)
  const contentRef = useRef<View>(null)
  const headerRef = useRef<View>(null)
  const internalTriggerRef = useRef<View>(null)

  // Reset body height synchronously when positionKey changes so the library
  // calculates position with unconstrained content, not the old constrained height.
  if (IS_WEB && prevPositionKey !== positionKey) {
    setPrevPositionKey(positionKey)
    setBodyMaxHeight(undefined)
  }

  const isRefTrigger = typeof trigger === 'object' && trigger !== null && 'current' in trigger

  const isControlled = isOpenProp !== undefined
  const isOpen = isControlled ? isOpenProp : isOpenState

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!isControlled) {
        setIsOpenState(open)
      }
      onOpenChange?.(open)
    },
    [isControlled, onOpenChange],
  )

  const triggerRef = isRefTrigger ? (trigger as React.RefObject<View>) : internalTriggerRef

  useOutsideClickClose(contentRef, triggerRef, isOpen, () => handleOpenChange(false))

  useEffect(() => {
    if (!IS_WEB || !isOpen) {
      setBodyMaxHeight(undefined)
      return
    }

    // Clear any constraint from a prior open while the observer hasn't fired yet.
    setBodyMaxHeight(undefined)

    const contentEl = contentRef.current as unknown as HTMLElement
    if (!contentEl) return

    // The library (react-native-popper) uses useLayoutEffect to set maxHeight on the
    // first position:absolute ancestor of our content, capping it to the available
    // viewport space. Walk up to find that element.
    let overlayEl: HTMLElement | null = contentEl.parentElement
    while (overlayEl && overlayEl !== document.body) {
      if (window.getComputedStyle(overlayEl).position === 'absolute') break
      overlayEl = overlayEl.parentElement
    }
    if (!overlayEl || overlayEl === document.body) return

    const capturedOverlay = overlayEl
    // ResizeObserver fires on layout changes, which happen before the open animation's
    // first paint frame. This means getBoundingClientRect() here sees the final,
    // un-scaled bounds — unlike a plain useEffect measurement which can run mid-animation.
    const observer = new ResizeObserver(() => {
      const maxH = parseFloat(window.getComputedStyle(capturedOverlay).maxHeight)
      const overlayHeight = isFinite(maxH) ? maxH : capturedOverlay.getBoundingClientRect().height
      const headerHeight = (headerRef.current as unknown as HTMLElement)?.getBoundingClientRect().height ?? 0
      setBodyMaxHeight(
        Math.max(MIN_SCROLL_BODY_HEIGHT, overlayHeight - headerHeight - CONTENT_PADDING * 2 - HEADER_GAP),
      )
    })
    observer.observe(capturedOverlay)

    return () => observer.disconnect()
    // positionKey is included so the observer re-attaches after the library re-mounts the overlay on key change.
  }, [isOpen, positionKey])

  return (
    <>
      {!isRefTrigger && <View ref={internalTriggerRef}>{trigger as React.ReactElement}</View>}
      <RNPopper
        key={positionKey}
        on='press'
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        trigger={isRefTrigger ? trigger : internalTriggerRef}
        offset={offset}
        crossOffset={crossOffset}
        placement={placement}
        animated={!window.Cypress}
        shouldCloseOnOutsideClick={false}
      >
        {!IS_WEB && <RNPopper.Backdrop />}
        <RNPopper.Content>
          <PopoverContentView ref={contentRef} testID={tID(`${testIdPrefix}-content`)} width={width}>
            {aboveHeaderContent}
            <HeaderRow ref={headerRef}>
              <Title size={TitleSize.MEDIUM} text={title} />
              <TertiaryIconButton
                testID={tID(`${testIdPrefix}-closeButton`)}
                onPress={() => handleOpenChange(false)}
                leftIcon={
                  <IconContainer>
                    <CloseIcon size={16} fillColor={colors.iconDefault} />
                  </IconContainer>
                }
                accessibilityLabel={formatMessage(closeAccessibilityLabel)}
                size={ButtonSize.SMALL}
              />
            </HeaderRow>
            <ScrollableContent style={bodyMaxHeight !== undefined ? { maxHeight: bodyMaxHeight } : undefined}>
              {children}
            </ScrollableContent>
          </PopoverContentView>
        </RNPopper.Content>
      </RNPopper>
    </>
  )
}
